import { usePermission } from "@/shared/permission";
import {
  useWarehouseStaffPermissionsContext,
  type IPerWarehousePermission
} from "../contexts/WarehouseStaffPermissionsContext";

/**
 * Role-level (code-based) staff permissions + row-level (per-warehouse) context.
 * - Role perms: PRT_WRH_STAFF_* code-uudaas read.
 * - Row perms: /me/warehouse-permissions context-aas.
 */
export function useWarehouseStaffPermissions(warehouseId?: string | null) {
  const list = usePermission("PRT_WRH_STAFF_LIST");
  const addKeeper = usePermission("PRT_WRH_STAFF_ADD_KEEPER");
  const addSub = usePermission("PRT_WRH_STAFF_ADD_SUB");
  const edit = usePermission("PRT_WRH_STAFF_EDIT");
  const remove = usePermission("PRT_WRH_STAFF_REMOVE");
  const manageFlag = usePermission("PRT_WRH_STAFF_MANAGE_FLAG");
  const bulk = usePermission("PRT_WRH_STAFF_BULK");

  const ctx = useWarehouseStaffPermissionsContext();
  const rowsForWarehouse: IPerWarehousePermission[] = warehouseId
    ? ctx.perWarehouseMap.get(warehouseId) ?? []
    : [];

  const isManager = ctx.isManager || addKeeper.hasAccess;
  const isStaffAtWarehouse = rowsForWarehouse.length > 0;
  const isKeeperWithDelegation = rowsForWarehouse.some(
    (r) => r.staffRole === "WAREHOUSE_KEEPER" && r.canManageStaff === true
  );

  return {
    list,
    addKeeper,
    addSub,
    edit,
    remove,
    manageFlag,
    bulk,
    isManager,
    isStaffAtWarehouse,
    isKeeperWithDelegation,
    rowsForWarehouse,
    refreshContext: ctx.refresh,
    contextLoading: ctx.loading
  };
}

export type WarehouseStaffPermissions = ReturnType<typeof useWarehouseStaffPermissions>;
